import styles from './Pagination.module.css'
import { Button, IconButton } from '../Button'
import { Select } from '../Select'

interface PaginationProps {
  page: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
  onPageChange: (page: number) => void
  perPage?: number
  onPerPageChange?: (perPage: number) => void
  perPageOptions?: number[]
}

export function Pagination({
  page,
  totalPages,
  hasNext,
  hasPrev,
  onPageChange,
  perPage,
  onPerPageChange,
  perPageOptions = [25, 50, 100],
}: PaginationProps) {
  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = []
    const showPages = 5 // Number of page buttons to show

    if (totalPages <= showPages + 2) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)

      // Calculate start and end of middle section
      let start = Math.max(2, page - 1)
      let end = Math.min(totalPages - 1, page + 1)

      // Adjust if near start or end
      if (page <= 3) {
        end = 4
      } else if (page >= totalPages - 2) {
        start = totalPages - 3
      }

      // Add ellipsis before middle section if needed
      if (start > 2) {
        pages.push('ellipsis')
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      // Add ellipsis after middle section if needed
      if (end < totalPages - 1) {
        pages.push('ellipsis')
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages)
      }
    }

    return pages
  }

  if (totalPages <= 1 && !onPerPageChange) {
    return null
  }

  return (
    <div className={styles.pagination}>
      {onPerPageChange && perPage && (
        <div className={styles.perPage}>
          <label htmlFor="per-page">Per page:</label>
          <Select
            id="per-page"
            value={perPage}
            onChange={(e) => onPerPageChange(Number((e.target as HTMLSelectElement).value))}
            options={perPageOptions.map(opt => ({ value: opt, label: String(opt) }))}
          />
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.buttons}>
          <IconButton
            variant="secondary"
            icon="bi bi-chevron-left"
            aria-label="Previous page"
            title="Previous page"
            onClick={() => onPageChange(page - 1)}
            disabled={!hasPrev}
          />

          {getPageNumbers().map((pageNum, index) =>
            pageNum === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className={styles.ellipsis}>
                ...
              </span>
            ) : (
              <Button
                key={pageNum}
                variant={pageNum === page ? 'primary' : 'secondary'}
                onClick={() => onPageChange(pageNum)}
                aria-current={pageNum === page ? 'page' : undefined}
              >
                {pageNum}
              </Button>
            )
          )}

          <IconButton
            variant="secondary"
            icon="bi bi-chevron-right"
            aria-label="Next page"
            title="Next page"
            onClick={() => onPageChange(page + 1)}
            disabled={!hasNext}
          />
        </div>
      )}
    </div>
  )
}
